import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_Z = 100_001;

export type InfoHelpBubbleProps = {
  content: ReactNode;
  position?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
  zIndex?: number;
  maxWidth?: number;
  className?: string;
  ariaLabel?: string;
};

/** Portal tooltip button; keep z-index above modal stacks across plugin bundles. */
const InfoHelpBubble = ({
  content,
  position = 'top',
  delay = 200,
  zIndex = DEFAULT_Z,
  maxWidth = 320,
  className = '',
  ariaLabel = 'Information',
}: InfoHelpBubbleProps) => {
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  const clearHide = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHide();
    hideTimerRef.current = window.setTimeout(() => {
      setVisible(false);
    }, 120);
  }, [clearHide]);

  const reposition = useCallback(() => {
    if (!triggerRef.current || !panelRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const p = panelRef.current.getBoundingClientRect();
    let top = 0;
    let left = 0;
    const pad = 8;
    switch (position) {
      case 'top':
        top = t.top - p.height - pad;
        left = t.left + t.width / 2 - p.width / 2;
        break;
      case 'bottom':
        top = t.bottom + pad;
        left = t.left + t.width / 2 - p.width / 2;
        break;
      case 'left':
        top = t.top + t.height / 2 - p.height / 2;
        left = t.left - p.width - pad;
        break;
      case 'right':
        top = t.top + t.height / 2 - p.height / 2;
        left = t.right + pad;
        break;
      default:
        break;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left < 8) left = 8;
    if (left + p.width > vw - 8) left = vw - p.width - 8;
    if (top < 8) top = 8;
    if (top + p.height > vh - 8) top = vh - p.height - 8;
    setCoords({ top, left });
  }, [position]);

  const show = useCallback(() => {
    clearHide();
    if (showTimerRef.current !== null) window.clearTimeout(showTimerRef.current);
    showTimerRef.current = window.setTimeout(() => {
      setVisible(true);
      window.requestAnimationFrame(() => reposition());
    }, delay);
  }, [delay, reposition, clearHide]);

  const hide = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    if (!visible) return undefined;
    const onMove = () => reposition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [visible, reposition]);

  useEffect(
    () => () => {
      if (showTimerRef.current !== null) window.clearTimeout(showTimerRef.current);
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    },
    [],
  );

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape' && visible) {
      e.preventDefault();
      setVisible(false);
    }
  };

  const panel =
    visible && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={tooltipId}
            role="tooltip"
            className={`tpct-info-bubble__panel tpct-info-bubble__panel--${position}`}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              zIndex,
              maxWidth,
              padding: '10px 12px',
              background: 'var(--color-white, #fff)',
              color: 'var(--color-neutral-400, #374151)',
              border: '1px solid var(--color-stroke, #e5e7eb)',
              borderRadius: 'var(--border-radius-md, 8px)',
              boxShadow: '0 8px 24px rgb(0 0 0 / 12%)',
              fontSize: '0.8125rem',
              lineHeight: 1.45,
            }}
            onMouseEnter={clearHide}
            onMouseLeave={scheduleHide}
          >
            {content}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`tpct-info-bubble__trigger ${className}`.trim()}
        aria-label={ariaLabel}
        aria-expanded={visible}
        aria-describedby={visible ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={onTriggerKeyDown}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          padding: 0,
          border: '1px solid var(--color-stroke, #d1d5db)',
          borderRadius: '50%',
          background: 'var(--color-form, #f9fafb)',
          color: 'var(--color-neutral-300, #6b7280)',
          cursor: 'help',
          flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
      </button>
      {panel}
    </>
  );
};

export default InfoHelpBubble;
